import {useState, ReactNode} from "react";
import {Box} from "@mui/material";
import ListFilters, {BaseFilters} from "./ListFilters";
import InfiniteScrollList from "./InfiniteScrollList";
import {useClientFiltering} from "../hooks/usePagination";

export interface FilterableListProps<T, F extends BaseFilters> {
  data: T[];
  loading?: boolean;
  error?: string | null;
  filters: F;
  onFiltersChange: (filters: F) => void;
  onClearFilters: () => void;
  renderItem: (item: T, index: number) => ReactNode;
  searchFields: (keyof T)[];
  additionalFilter?: (item: T, filters: F) => boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  children?: ReactNode; // Additional filter controls
  customActiveFilters?: ReactNode;
  useInfiniteScroll?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  renderSkeleton?: () => ReactNode;
  skeletonCount?: number;
  filtersStartCollapsed?: boolean; // New prop to control initial filter visibility
}

/**
 * Combined component that provides filtering and infinite scroll functionality
 * Can work with both client-side filtering and server-side pagination
 */
function FilterableList<T, F extends BaseFilters>({
  data,
  loading = false,
  error = null,
  filters,
  onFiltersChange,
  onClearFilters,
  renderItem,
  searchFields,
  additionalFilter,
  searchPlaceholder,
  emptyMessage,
  children,
  customActiveFilters,
  useInfiniteScroll = false,
  hasMore = false,
  onLoadMore,
  renderSkeleton,
  skeletonCount,
  filtersStartCollapsed = false,
}: FilterableListProps<T, F>) {
  const [filtersExpanded, setFiltersExpanded] = useState(
    !filtersStartCollapsed
  );

  const toggleFilters = () => {
    setFiltersExpanded((prev) => !prev);
  };

  // Client-side filtering (when not using infinite scroll with server pagination)
  const filteredData = useClientFiltering(
    data,
    filters.search,
    searchFields,
    additionalFilter ? (item: T) => additionalFilter(item, filters) : undefined
  );

  // Use filtered data for client-side filtering, raw data for server-side pagination
  const displayData = useInfiniteScroll ? data : filteredData;

  return (
    <Box sx={{height: "100%", display: "flex", flexDirection: "column"}}>
      {/* Filters */}
      <ListFilters
        filters={filters}
        onFiltersChange={onFiltersChange}
        onClearFilters={onClearFilters}
        expanded={filtersExpanded}
        onToggleFilters={toggleFilters}
        searchPlaceholder={searchPlaceholder}
        customActiveFilters={customActiveFilters}
        startCollapsed={filtersStartCollapsed}
      >
        {children}
      </ListFilters>

      {/* List */}
      <Box sx={{flex: 1, minHeight: 0}}>
        <InfiniteScrollList
          data={displayData}
          loading={loading}
          error={error}
          hasMore={useInfiniteScroll ? hasMore : false}
          onLoadMore={onLoadMore || (() => {})}
          renderItem={renderItem}
          emptyMessage={emptyMessage}
          renderSkeleton={renderSkeleton}
          skeletonCount={skeletonCount}
        />
      </Box>
    </Box>
  );
}

export default FilterableList;
